import React from 'react';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Link from '@amzn/awsui-components-console/link';
import Icon from '@amzn/awsui-components-console/icon';
import Button from '@amzn/awsui-components-console/button';
import ColumnLayout from '@amzn/awsui-components-console/column-layout';
import './ApplicationDetailCard.scss';

export interface DataSource {
  id: string;
  label: string;
}

export interface ApplicationDetailCardProps {
  applicationId: string;
  createdAt: string;
  subscriptionCount: number;
  lastUpdatedAt: string;
  lastUpdatedBy: string;
  dataSources: DataSource[];
  onDataSourceClick?: (dataSourceId: string) => void;
  onManagementClick?: () => void;
  isAdmin?: boolean;
}

const ApplicationDetailCard: React.FC<ApplicationDetailCardProps> = ({
  applicationId,
  createdAt,
  subscriptionCount,
  lastUpdatedAt,
  lastUpdatedBy,
  dataSources,
  onDataSourceClick,
  onManagementClick,
  isAdmin = true,
}) => {
  // Format the createdAt date to match Figma design: "Joined Jan 15, 2024"
  const formatDate = (isoDate: string): string => {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Format the lastUpdatedAt to a readable format
  const formatDateTime = (isoDate: string): string => {
    const date = new Date(isoDate);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="application-detail-card">
      <SpaceBetween size="l">
        {/* Header with title, join date, and management button */}
        <div className="application-detail-card__header">
          <div className="application-detail-card__title-section">
            <h1 className="application-detail-card__title">{applicationId}</h1>
            <p className="application-detail-card__subtitle">Joined {formatDate(createdAt)}</p>
          </div>
          <Button
            iconName="folder-open"
            variant="normal"
            disabled={!isAdmin}
            disabledReason={!isAdmin ? 'Admin role required to manage applications' : undefined}
            onClick={onManagementClick}
          >
            Management
          </Button>
        </div>

        {/* Key-value pairs in columns */}
        <div className="application-detail-card__metadata">
          <ColumnLayout columns={3} variant="text-grid">
            <div className="application-detail-card__key-value">
              <div className="application-detail-card__key">Subscribed Content Collections #</div>
              <div className="application-detail-card__value">{subscriptionCount}</div>
            </div>
            <div className="application-detail-card__key-value">
              <div className="application-detail-card__key">Last Update time</div>
              <div className="application-detail-card__value">{formatDateTime(lastUpdatedAt)}</div>
            </div>
            <div className="application-detail-card__key-value">
              <div className="application-detail-card__key">Updated by</div>
              <div className="application-detail-card__value">{lastUpdatedBy}</div>
            </div>
          </ColumnLayout>
        </div>

        {/* Subscriptions section */}
        {dataSources.length > 0 && (
          <div className="application-detail-card__subscriptions">
            <SpaceBetween size="s">
              <div className="application-detail-card__subscriptions-label">Subscriptions</div>
              <div className="application-detail-card__data-sources">
                <SpaceBetween size="m" direction="horizontal">
                  {dataSources.map((dataSource) => (
                    <div key={dataSource.id} className="application-detail-card__data-source-item">
                      <Icon name="folder-open" variant="link" />
                      <Link
                        variant="primary"
                        onFollow={(e) => {
                          e.preventDefault();
                          onDataSourceClick?.(dataSource.label);
                        }}
                      >
                        {dataSource.label}
                      </Link>
                    </div>
                  ))}
                </SpaceBetween>
              </div>
            </SpaceBetween>
          </div>
        )}
      </SpaceBetween>
    </div>
  );
};

export default ApplicationDetailCard;
